/**
 * RettshjelpOversikt - Oversikt over alle saker med rettshjelpsdekning
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './RettshjelpOversikt.css';

export default function RettshjelpOversikt({ navigerTil }) {
  const [saker, setSaker] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('alle'); // 'alle', 'bkf', 'innbo'

  useEffect(() => {
    lastRettshjelpSaker();
  }, []);

  const lastRettshjelpSaker = async () => {
    try {
      setLoading(true);
      // Hent alle saker
      const data = await crmApi.getSaker();

      // API returnerer enten direkte array eller { saker: [...] }
      const alleSaker = Array.isArray(data) ? data : (data.saker || []);

      // Filtrer ut de med rettshjelp (sjekk både ID og navn)
      const medRettshjelp = alleSaker.filter(s =>
        s.rettshjelp_bkf_selskap_id || s.rettshjelp_innbo_selskap_id ||
        s.rettshjelp_bkf_selskap_navn || s.rettshjelp_innbo_selskap_navn
      );

      setSaker(medRettshjelp);
    } catch (error) {
      console.error('Feil ved lasting av rettshjelp-saker:', error);
      alert('Kunne ikke laste rettshjelp-oversikt');
    } finally {
      setLoading(false);
    }
  };

  const filtrerteSaker = saker.filter(s => {
    if (filter === 'bkf') return s.rettshjelp_bkf_selskap_id || s.rettshjelp_bkf_selskap_navn;
    if (filter === 'innbo') return s.rettshjelp_innbo_selskap_id || s.rettshjelp_innbo_selskap_navn;
    return true; // 'alle'
  });

  if (loading) {
    return (
      <div className="rettshjelp-oversikt loading">
        <div className="spinner"></div>
        <p>Laster rettshjelp-oversikt...</p>
      </div>
    );
  }

  return (
    <div className="rettshjelp-oversikt">
      <div className="rettshjelp-header">
        <div>
          <h1>🛡️ Oversikt rettshjelp</h1>
          <p className="subtitle">{saker.length} saker med aktiv rettshjelpsdekning</p>
        </div>
        {navigerTil && (
          <button
            className="btn-vis-uten-dekning"
            onClick={() => navigerTil('rettshjelp-uten')}
          >
            🔍 Vis saker uten rettshjelp
          </button>
        )}
      </div>

      <div className="filter-buttons">
        <button
          className={`filter-btn ${filter === 'alle' ? 'active' : ''}`}
          onClick={() => setFilter('alle')}
        >
          Alle ({saker.length})
        </button>
        <button
          className={`filter-btn ${filter === 'bkf' ? 'active' : ''}`}
          onClick={() => setFilter('bkf')}
        >
          Boligkjøperforsikring ({saker.filter(s => s.rettshjelp_bkf_selskap_id || s.rettshjelp_bkf_selskap_navn).length})
        </button>
        <button
          className={`filter-btn ${filter === 'innbo' ? 'active' : ''}`}
          onClick={() => setFilter('innbo')}
        >
          Innbo/Bygg ({saker.filter(s => s.rettshjelp_innbo_selskap_id || s.rettshjelp_innbo_selskap_navn).length})
        </button>
      </div>

      {filtrerteSaker.length === 0 ? (
        <div className="empty-state">
          <p>Ingen saker funnet med {filter === 'bkf' ? 'boligkjøperforsikring' : filter === 'innbo' ? 'innbo/byggforsikring' : 'rettshjelp'}</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="rettshjelp-table">
            <thead>
              <tr>
                <th>Saksnr</th>
                <th>Klient</th>
                <th>🏠 Boligkjøperforsikring (BKF)</th>
                <th>🏢 Innbo/Bygg</th>
              </tr>
            </thead>
            <tbody>
              {filtrerteSaker.map(sak => (
                <tr key={sak.id}>
                  <td><strong>{sak.saksnummer}</strong></td>
                  <td>
                    <div className="klient-info">
                      {sak.klient_navn || '-'}
                    </div>
                  </td>

                  {/* Boligkjøperforsikring */}
                  <td>
                    {(sak.rettshjelp_bkf_selskap_id || sak.rettshjelp_bkf_selskap_navn) ? (
                      <div className="forsikring-detaljer">
                        <div className="badge badge-success">✓ BKF</div>
                        <div className="detaljer">
                          <p><strong>Selskap:</strong> {sak.rettshjelp_bkf_selskap_navn || '-'}</p>
                          {sak.rettshjelp_bkf_epost && (
                            <p><strong>E-post:</strong> <a href={`mailto:${sak.rettshjelp_bkf_epost}`}>{sak.rettshjelp_bkf_epost}</a></p>
                          )}
                          {sak.rettshjelp_bkf_vilkar && (
                            <p><strong>Vilkår:</strong> {sak.rettshjelp_bkf_vilkar}</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="badge badge-inactive">-</span>
                    )}
                  </td>

                  {/* Innbo/Byggforsikring */}
                  <td>
                    {(sak.rettshjelp_innbo_selskap_id || sak.rettshjelp_innbo_selskap_navn) ? (
                      <div className="forsikring-detaljer">
                        <div className="badge badge-success">✓ Innbo/Bygg</div>
                        <div className="detaljer">
                          <p><strong>Selskap:</strong> {sak.rettshjelp_innbo_selskap_navn || '-'}</p>
                          {sak.rettshjelp_innbo_epost && (
                            <p><strong>E-post:</strong> <a href={`mailto:${sak.rettshjelp_innbo_epost}`}>{sak.rettshjelp_innbo_epost}</a></p>
                          )}
                          {sak.rettshjelp_innbo_dekningsdato && (
                            <p><strong>Dekningsdato:</strong> {sak.rettshjelp_innbo_dekningsdato}</p>
                          )}
                          {sak.rettshjelp_innbo_vilkar && (
                            <p><strong>Vilkår:</strong> {sak.rettshjelp_innbo_vilkar}</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="badge badge-inactive">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="summary">
        <h3>📊 Oppsummering</h3>
        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-number">{saker.filter(s => s.rettshjelp_bkf_selskap_id || s.rettshjelp_bkf_selskap_navn).length}</div>
            <div className="summary-label">Boligkjøperforsikring</div>
          </div>
          <div className="summary-card">
            <div className="summary-number">{saker.filter(s => s.rettshjelp_innbo_selskap_id || s.rettshjelp_innbo_selskap_navn).length}</div>
            <div className="summary-label">Innbo/Bygg</div>
          </div>
          <div className="summary-card">
            <div className="summary-number">{saker.filter(s => (s.rettshjelp_bkf_selskap_id || s.rettshjelp_bkf_selskap_navn) && (s.rettshjelp_innbo_selskap_id || s.rettshjelp_innbo_selskap_navn)).length}</div>
            <div className="summary-label">Begge typer</div>
          </div>
          <div className="summary-card">
            <div className="summary-number">{saker.length}</div>
            <div className="summary-label">Totalt</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function for å få forsikringsselskap navn
function getForsikringsselskap(id) {
  const selskaper = {
    'if': 'If Skadeforsikring',
    'tryg': 'Tryg Forsikring',
    'gjensidige': 'Gjensidige',
    'fremtind': 'Fremtind',
    'protector': 'Protector Forsikring',
    'sparebank1': 'SpareBank 1 Forsikring',
    'dnb': 'DNB Forsikring',
    'storebrand': 'Storebrand',
    'codan': 'Codan Forsikring',
    'frende': 'Frende Forsikring'
  };

  return selskaper[id] || id || 'Ukjent';
}
